import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { Fragment, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { company } from '~/company/Company';
import BannerMainBlockSkeleton from '~/components/CategoryBlocks/BannerMainBlock/BannerMainBlockSkeleton';
import BannerProductBlockSkeleton from '~/components/CategoryBlocks/BannerProductBlock/BannerProductBlockSkeleton';
import CategoriesGridOneSBlockSkeleton from '~/components/CategoryBlocks/CategoriesGridOneSBlock/CategoriesGridOneSBlockSkeleton';
import CategoryBlock, {
  CategoryDisplayType,
} from '~/components/CategoryBlocks/CategoryBlock';
import ProductsCarouselSliderOneBlockSkeleton from '~/components/CategoryBlocks/ProductsCarouselSliderOneBlock/ProductsCarouselSliderOneBlockSkeleton';
import ProductsGridBlockSkeleton from '~/components/CategoryBlocks/ProductsGridBlock/ProductsGridBlockSkeleton';
import LanguageSelector from '~/components/LanguageSelector/LanguageSelector';
import { ModalType } from '~/components/Modal/interface';
import { NoSsr } from '~/components/NoSsr';
import { useDateLang } from '~/hooks/useDateLang';
import { useModal } from '~/hooks/useModal';
import { useStores } from '~/hooks/useStore';
import { useTimeFormat } from '~/hooks/useTimeFormat';
import { DeliveryBanner } from '~/pages/Main/DeliveryBanner';
import { ScrollPaginationSkeleton } from '~/pages/Main/ScrollPaginationSkeleton';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { orderStore } from '~/stores';
import { checkAndSaveReferralPromocode } from '~/utils/referralLink';

import styles from './Main.module.scss';
import MobileHeaderAddress from './MobileHeader/MobileHeaderAddress';
import MobileHeaderEta from './MobileHeader/MobileHeaderEta';
import MobileHeaderLayout from './MobileHeader/MobileHeaderLayout';
import MobileLogo from './MobileHeader/MobileLogo';

const Main = () => {
  const { openModal } = useModal();
  const { t } = useTranslation();
  const { state, pathname, search } = useLocation();
  const navigate = useNavigate();
  const { homeCategories } = useStores();
  const { timeFormat } = useTimeFormat();
  const { locale } = useDateLang();

  useEffect(() => {
    if (state?.isShowAuthModal) {
      openModal(ModalType.Auth);
    } else if (state?.isShowDeliveryModal) {
      openModal(ModalType.Auth, 'auth', {}, true);
    }
  }, [state?.isShowAuthModal, state?.isShowDeliveryModal]);

  useEffect(() => {
    (async () => {
      analyticsService.track(AnalyticsEvent.CATALOG_HOME_PAGE_VISITED, {
        warehouse_code: orderStore.etaWarehouseCode,
      });

      const urlSearchParams = await checkAndSaveReferralPromocode(search);

      if (urlSearchParams) {
        navigate(
          {
            pathname,
            search: urlSearchParams.toString(),
          },
          { replace: true },
        );
      }
    })();
  }, []);

  const loadMore = useCallback(() => {
    homeCategories.paginationRequest.loadMore(orderStore.etaWarehouseCode);
  }, []);

  const skeleton = (
    <>
      <BannerMainBlockSkeleton />
      <ProductsCarouselSliderOneBlockSkeleton />
      <CategoriesGridOneSBlockSkeleton />
      <ProductsCarouselSliderOneBlockSkeleton />
      <BannerProductBlockSkeleton />
      <ProductsGridBlockSkeleton />
    </>
  );

  const {
    message: expressDeliveryMessage,
    args: expressDeliveryMessageArgs,
    showFlashIcon,
  } = orderStore.getDeliveryLabel({ timeFormat, locale });

  const renderDeliveryBanner = () => {
    if (!company.showExpressDeliveryBannerMainPage) {
      return;
    }

    // if (isMobile) {
    //   return <ExpressDeliveryBannerMobile />;
    // }
    // return <ExpressDeliveryBannerDesktop />;

    return (
      <div className="content-layout">
        <DeliveryBanner />
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>{t('phrases:mainMetaTitle')}</title>
        <meta name="description" content={t('phrases:mainMetaDescription')} />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div
        className={classes(['page-main', styles.root])}
        data-company={company.name}
      >
        <MobileLogo />
        <MobileHeaderLayout
          className={styles.mobileHeaderLayoutClassName}
          isSticky={true}
          content={
            <>
              <MobileHeaderAddress headerColor />
              <div className={styles.headerGroup}>
                {company.showETAInfoMobileHeader && (
                  <MobileHeaderEta
                    {...{
                      expressDeliveryMessage,
                      expressDeliveryMessageArgs,
                      showFlashIcon,
                    }}
                  />
                )}
                <LanguageSelector
                  dropdownClassNames={styles.languageSelector}
                />
              </div>
            </>
          }
        />
        <div className={styles.container}>
          {renderDeliveryBanner()}
          {homeCategories.list.length === 0
            ? skeleton
            : homeCategories.list.map((category) => {
                return (
                  <Fragment key={category.id}>
                    <CategoryBlock
                      className={classes([
                        category.displayType !==
                          CategoryDisplayType.CATEGORIES_CAROUSEL_SLIDER_TWO &&
                        category.displayType !==
                          CategoryDisplayType.CATEGORIES_CAROUSEL_SQUARE_ONE
                          ? 'content-layout'
                          : undefined,
                        (CategoryDisplayType.MAIN_BANNER ===
                          category.displayType ||
                          CategoryDisplayType.BANNER_CATEGORY ===
                            category.displayType) &&
                          styles.overflowXHidden,
                      ])}
                      category={category}
                      externalLink={category.externalLink ?? undefined}
                    />
                  </Fragment>
                );
              })}
          <NoSsr>
            {homeCategories.list.length > 0 &&
              homeCategories.paginationRequest.canLoadMore && (
                <ScrollPaginationSkeleton
                  className={styles.scrollPaginationSkeleton}
                  onLoadMore={loadMore}
                />
              )}
          </NoSsr>
        </div>
      </div>
    </>
  );
};

export default observer(Main);
